import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useEmergencyContacts } from "../../contexts/EmergencyContacts";
import Modal from "../../components/UI/Modal";
import EmergencyContactsForm from "../../components/Form/EmergencyContactsForm";
import ReusableTable from "../../components/UI/ReusableTable";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import ReactPaginate from 'react-paginate';
import { MoreVertical } from 'lucide-react';

const EmergencyContacts = React.memo(() => {
    const { emergencyContacts, fetchEmergencyContacts, addEmergencyContact, updateEmergencyContact, deleteEmergencyContact, pagination, isLoading } = useEmergencyContacts();

    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingContacts, setEmergencyContacts] = useState(null);
    const [message, setMessage] = useState(""); // Success message
    const [formErrors, setFormErrors] = useState({}); // Validation errors
    const [notification, setNotification] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [isFiltering, setIsFiltering] = useState(false);
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState('bottom');
    
    // console.log("contacts: ",emergencyContacts)

    // Handlers
    const openModalForAdd = useCallback(() => {
        setEmergencyContacts(null);
        setIsModalOpen(true);
        setMessage("");
        setFormErrors({});
    }, []);

    // const openModalForBulkAdd = useCallback(() => {
    //   setShowModal(true);
    // }, []);

    const openModalForEdit = useCallback((emergency_contact) => {
        // console.log("edit workplace: ", emergency_contact)
        setEmergencyContacts(emergency_contact);
        setIsModalOpen(true);
        setMessage("");
        setFormErrors({});
    }, []);

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
        setEmergencyContacts(null);
        setFormErrors({});
        setMessage("");
    }, []);

    const convertFormDataToObject = (formData) => {
        return Object.fromEntries(formData.entries());
    };

    // create and update function
    const handleFormSubmit = useCallback(
        async (contact) => {
        setLoading(true);
        try {
            if (editingContacts) {
                // console.log("emergency contact payload", contact);
            
                // Convert FormData to object
                // console.log(contact instanceof FormData)
                const contactData = contact instanceof FormData ? convertFormDataToObject(contact) : contact;
    
                // console.log("Payload being sent:", { ...editingContacts, ...contactData });
    
                await updateEmergencyContact({ ...editingContacts, ...contactData });
                setMessage("Emergency contact updated successfully.");
                // setNotification("Emergency contact updated successfully.");
                setNotification({
                    message: "Emergency contact updated successfully.",
                    type: "success",
                });
            } else {
            try {
                // Convert FormData to object
                await addEmergencyContact(contact);
                setMessage("Emergency Contact added successfully.");
                // setNotification("Emergency Contact added successfully.");
                setNotification({
                    message: "Emergency Contact added successfully.",
                    type: "success",
                });
            } catch (error) {
                console.error("Error adding Emergency contact:", error.message);
                // setNotification(error.message);
                setNotification({
                    message: error.message,
                    type: "error",
                });
            }
            }
            closeModal();
        } catch (err) {
            if (err.errors) {
                setFormErrors(err.errors);
                // setNotification(err.message);
                setNotification({
                    message: err.message,
                    type: "error",
                });
            } else {
                // console.error("Unexpected error during submission:", err);
                // setNotification("Unexpected error during submission:", err);
                setNotification({
                    message: "Unexpected error during submission:", err,
                    type: "error",
                });
            }
        } finally {
            setLoading(false);
        }
        },
        [editingContacts, addEmergencyContact, updateEmergencyContact, closeModal]
    );

    // Memoize contacts
    const contactForTable = useMemo(
        () =>
          emergencyContacts.map((contact) => ({
            ...contact,
            employee_id: contact?.employee_id,
            contact_name: contact?.contact_name,
            relationship: contact?.relationship,
            contact_phone: contact?.contact_phone,
            contact_email: contact?.contact_email,
          })),
        [emergencyContacts]
      );
      

    // delete function
    const handleDeleteContact = async (id) => {
        try {
        await deleteEmergencyContact(id);
        // setNotification("Contact deleted successfully");
        setNotification({
            message: "Contact deleted successfully",
            type: "success",
        });
        } catch (error) {
        // setNotification("Failed to delete contact");
        setNotification({
            message: "Failed to delete contact",
            type: "error",
        });
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openDropdownId && !event.target.closest('.dropdown-container')) {
                setOpenDropdownId(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openDropdownId]);

    // Function to determine dropdown position
    const handleDropdownToggle = (contactId, event) => {
        if (openDropdownId === contactId) {
            setOpenDropdownId(null);
        } else {
            const button = event.currentTarget;
            const rect = button.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const dropdownHeight = 150; // Approximate dropdown height
            
            // Check if there's enough space below
            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;
            
            if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
                setDropdownPosition('top');
            } else {
                setDropdownPosition('bottom');
            }
            
            setOpenDropdownId(contactId);
        }
    };

    // Table columns
    const columns = useMemo(
        () => [
        { label: "Employee ID", field: "employee_id" },
        { label: "Contact Name", field: "contact_name" },
        { label: "Relationship", field: "relationship" },
        { label: "Contact Phone", field: "contact_phone" },
        { label: "Contact Email", field: "contact_email" },
        {
            label: "Actions",
            field: "actions",
            render: (row) => (
                <div className="flex justify-center items-center">
                    <div className="relative dropdown-container">
                        <button
                            onClick={(e) => {
                                // console.log('Row data for dropdown:', row);
                                handleDropdownToggle(row.id || row.contact_id, e);
                            }}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                        >
                            <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        </button>
                    
                        {openDropdownId === (row.id || row.contact_id) && (
                            <div className={`absolute right-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-[9999] min-w-[200px] ${
                                dropdownPosition === 'top' 
                                    ? 'bottom-full mb-1' 
                                    : 'top-full mt-1'
                            }`} style={{
                                maxHeight: '300px',
                                overflowY: 'auto'
                            }}>
                                <div className="py-1 flex flex-col space-y-0">
                                    {/* Edit */}
                                    <button
                                        onClick={() => {
                                            openModalForEdit(row);
                                            setOpenDropdownId(null);
                                        }}
                                        className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors block"
                                    >
                                        Edit
                                    </button>

                                    {/* Delete */}
                                    <DeleteButton
                                        style={`text-red-600 hover:text-red-800 px-3 py-2 w-full text-left text-sm hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors block`}
                                        onDelete={() => {
                                            handleDeleteContact(row.id || row.contact_id);
                                            setOpenDropdownId(null);
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            ),
        },
        ],
        [openModalForEdit, openDropdownId, dropdownPosition, handleDropdownToggle]
    );
  
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setCurrentPage(newPage);
            fetchEmergencyContacts(newPage);
        }
    };

    const renderPageNumbers = () => {
        const pages = [];
        for (let i = 1; i <= pagination.totalPages; i++) {
            pages.push(
            <button
                key={i}
                onClick={() => handlePageChange(i)}
                className={`px-4 py-2 rounded-md mx-1 ${
                currentPage === i ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
            >
                {i}
            </button>
            );
        }
        return pages;
    };

    return (
        <div className="p-4 sm:p-6 bg-white dark:bg-gray-900 font-poppins rounded-lg">
            
            {/* notification compo */}
            {notification && (
                <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
            )}

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <h2 className="text-2xl font-semibold text-black dark:text-white">Emergency Contacts</h2>
                <div className="flex gap-2">
                </div>
            </div>

            {/* Success Message */}
            {message && (
                <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-md">
                {message}
                </div>
            )}

            {/* Candidates Table */}
            <ReusableTable
                data={contactForTable}
                loading={isLoading}
                columns={columns}
                filterable={true}
                excludeFilterColumns={["actions"]}
                onFilterChange={(filtering) => setIsFiltering(filtering)}
            />

            {/* Pagination */}
            {!isFiltering && contactForTable?.length > 0 && (
                <ReactPaginate
                    breakLabel="..."
                    onPageChange={(event) => handlePageChange(event.selected + 1)}
                    forcePage={currentPage - 1}
                    pageRangeDisplayed={5}
                    pageCount={pagination.totalPages}
                    nextLabel="Next >"
                    previousLabel="< Prev"
                    previousClassName='dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800'
                    nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
                    renderOnZeroPageCount={null}
                    containerClassName="flex items-center justify-center mt-4"
                    pageClassName="mx-1 px-3 py-1 border rounded dark:text-white"
                    activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700"
                />
            )}


            {/* Modal for Adding or Editing */}
            <Modal
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                title={editingContacts ? "Edit Emergency Contact" : "Add Emergency Contact"}>

                <EmergencyContactsForm
                loading={loading}
                closeModal={closeModal}
                onSubmit={handleFormSubmit}
                initialValues={editingContacts || { employee_id: "", contact_name: "", relationship: "", contact_phone: "", contact_email: "" }}
                errors={formErrors}
                />
            </Modal>

        </div>
    );
});

export default EmergencyContacts;
